/** Collection is like a flat redux store slice. It's an array of models that can be */
import { cloneDeep } from '../utils/object.js';
/** Subscribed to. On every change it returns a fresh array to observer */
export class SDKCollection extends Array {
    constructor(...args) {
        super(...args);
        this.observers = [];
        this.defineProperties();
    }
    defineProperties() {
        Object.defineProperty(this, 'snapshotted', {
            enumerable: false,
            writable: true
        });
        Object.defineProperty(this, 'observers', {
            enumerable: false,
            value: []
        });
    }
    get first() {
        return this[0];
    }
    onChange() {
        this.onCollectionChange();
        this.observers.forEach((observer) => observer.call(this, this), this);
    }
    onCollectionChange() {
        this.model.onCollectionChange(this);
    }
    observe(observer) {
        this.observers.push(observer);
    }
    unobserve(observer) {
        const index = this.observers.indexOf(observer);
        if (index > -1)
            this.observers.splice(index, 1);
    }
    getId(object) {
        return object.getId();
    }
    newInstance() { }
    /** Construct instance of a collection model */
    construct(object, isNew = false) {
        const clone = new this.model();
        /** Define properties that model inherits from collection */
        clone.sdk.utils.defineImplicitAccessors(clone, this.getImplicitAttributes, clone.getHiddenProps());
        /** Copy hidden properties that may need copying from given object */
        if (object)
            clone.copyHiddenProps(object, false);
        clone.set(object);
        /** Set New flag */
        if (isNew) {
            clone.setDefaults();
            clone.isNew = true;
        }
        /** Run transformation hook */
        clone.set(this.transformAttributes(clone));
        return clone;
    }
    transformAttributes(object) {
        if (this.model)
            return this.model.transformAttributes(object);
        return object;
    }
    getImplicitAttributes() {
        return {};
    }
    /** Instantiate model with default values */
    new(object) {
        return this.construct(object, true);
    }
    /** Instantiate model and add it to collection */
    add(object) {
        return this.addItem(this.new(object));
    }
    /** Instantiate model and send create request without adding to collection INSERT is alias */
    post(object) {
        return this.new(object).post();
    }
    insert(object) {
        return this.post(object);
    }
    /** PUT item (with all attributes), UPDATE as alias */
    put(data) {
        return this.construct(data).put();
    }
    update(data) {
        return this.put(data);
    }
    save(data) {
        return this.construct(data, true).save();
    }
    withId(target) {
        return this.construct(typeof target == 'object' ? target : { id: target });
    }
    /** DELETE item (or by id) */
    delete(target) {
        return this.withId(target).delete();
    }
    /** GET item (or by id) */
    get(target) {
        return this.withId(target).get();
    }
    /** GET ALL items, SELECT as alias */
    fetch(options) {
        return this.construct()
            .fetch(options)
            .then((items) => this.setItems(items));
    }
    select(options) {
        return this.fetch(options);
    }
    /** GET ALL items matching criteria */
    search(options) {
        return this.construct().search(options);
    }
    /** Replace all items in collections with items in given array */
    setItems(objects) {
        this.splice(0, this.length, ...objects.map((v) => this.construct(v), this));
        this.snapshot();
        this.onChange();
        return this;
    }
    /** Updates or adds item to collection */
    upsertItem(object) {
        const model = this.construct(object);
        const index = this.findIndex((other) => this.getId(other) === this.getId(model));
        if (index == -1) {
            this.push(model);
        }
        else {
            this.splice(index, 1, model);
        }
        this.onChange();
        return model;
    }
    /** Adds new item into a collection */
    addItem(object) {
        const model = this.construct(object);
        const index = this.findIndex((other) => this.getId(other) === this.getId(model));
        if (index != -1) {
            throw new Error('Trying to add object that is in collection already. Use upsertItem if you want to allow updating object.');
        }
        this.push(model);
        this.onChange();
        return model;
    }
    /** Patches the item in collection with matching id */
    updateItem(changes) {
        const index = this.indexOf(this.getItem(changes));
        if (index == -1)
            throw new Error('Trying to update object that is not in collection');
        this.splice(index, 1, this.construct(this[index].clone(changes)));
        this.onChange();
        return this[index];
    }
    /** Swaps item for another within collection */
    replaceItem(changes) {
        const index = this.indexOf(this.getItem(changes));
        if (index == -1)
            throw new Error('Trying to replace object that is not in collection');
        this.splice(index, 1, this.construct(changes));
        this.onChange();
        return this[index];
    }
    /** Removes item from collection */
    removeItem(target) {
        const index = this.indexOf(this.getItem(target));
        if (index === -1)
            throw new Error('Trying to delete object that is not in collection');
        var removed = this[index];
        this.splice(index, 1);
        this.onChange();
        return removed;
    }
    /** Gets item in collection by id or by id in object */
    getItem(target) {
        var id = typeof target == 'object' ? this.getId(this.construct(target)) : target;
        return this.find((object) => this.getId(object) === id);
    }
    /** Return plain array with models exported as plain objects */
    export() {
        return this.map((model) => model.export());
    }
    /** Return instance of collection with new identity, but fully refering to current one */
    proxy() {
        return new Proxy(this, {});
    }
    /**
     * Define a collection of given models. During construction of models, the attributes returned by second callback will
     * be used as implicitly provided values
     */
    static construct(model, getImplicitAttributes) {
        const collection = new this();
        Object.defineProperty(collection, 'model', {
            value: model,
            enumerable: false
        });
        Object.defineProperty(collection, 'getImplicitAttributes', {
            value: getImplicitAttributes,
            enumerable: false
        });
        return collection;
    }
    snapshot() {
        this.snapshotted = this.export();
        return this;
    }
    prependItem(object) {
        const model = this.construct(object);
        const index = this.findIndex((other) => this.getId(other) === this.getId(model));
        if (index != -1) {
            throw new Error('Trying to add object that is in collection already. Use upsertItem if you want to allow updating object.');
        }
        this.unshift(model);
        this.onChange();
        return model;
    }
}
/** An observable collection that does not have a class assigned to it. It simply calls a function on each item */
export class SDKFunctionalCollection extends SDKCollection {
    constructor(transformAttributes, getId) {
        super();
        this.transformAttributes = transformAttributes;
        this.getId = getId;
    }
    construct(object, isNew = false) {
        return this.transformAttributes(object);
    }
    export() {
        return cloneDeep(this);
    }
    onCollectionChange() {
        return this;
    }
}
//# sourceMappingURL=collection.js.map